import React, { useMemo, useState } from 'react'
import {
    Card,
    CardContent,
    Typography,
    Theme,
    makeStyles,
    TextField,
    Button,
    FormControl,
    useTheme,
    Tooltip,
    Switch,
    Collapse,
} from '@material-ui/core'
import {
    DatePicker,
    KeyboardDatePicker,
    MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { ChromePicker } from 'react-color'
import InfoIcon from '@material-ui/icons/Info'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

import { ChatMessageContent, ProjectResponse } from '../../../types'
import { useLoriState } from '../../../../../../contexts/LoriContext'
import { useSchedule } from '../../../../../../hooks/useSchedule'
import { Container, Selector } from '../../../../../../components'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../../store'
import { Inspection, Schedule } from '../../../../../../models'
import { useUser } from '../../../../../../hooks'
import { toYYYYMMDD } from '../../../../../../helpers'
import { typographyStyle } from '../../../../../../theme/typographyStyle'
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderLeft: '4px solid',
        borderBottom: '4px solid',
        borderRight: '1px solid',
        borderTop: '1px solid',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
    },
    header: {
        display: 'flex',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: theme.spacing(2),
    },
    projectName: {
        fontWeight: 700,
        fontSize: '1.1rem',
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    formControl: {
        width: '100%',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    colorPickerContainer: {
        marginBottom: theme.spacing(2),
    },
    colorPreview: {
        width: '100%',
        height: '20px',
        marginTop: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        border: '1px solid #ddd',
    },
    datePickers: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    datePicker: {
        flex: 1,
    },
}))

interface Props {
    message: ChatMessageContent
}

export const CreateProjectCard: React.FC<Props> = ({ message }) => {
    const { workspaceUser } = useUser()

    if (!message.function_call_response) return null

    const theme = useTheme()
    const classes = useStyles(theme)
    const { handleSendMessage, setThinking, setShouldRefresh } = useLoriState()
    const {
        createSchedule,
        getUnitReport,
        syncBedStatuses,
        syncBedStatusesFromLeases,
    } = useSchedule()

    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    // Extract project data from function_call_response if available
    const projectResponse = message.function_call_response as ProjectResponse

    const [projectName, setProjectName] = useState(
        projectResponse?.projectName || '',
    )
    const [projectId, setProjectId] = useState(projectResponse?.projectId || '')
    const [projectBudget, setProjectBudget] = useState(
        projectResponse?.projectBudget || '',
    )
    const [projectColor, setProjectColor] = useState(
        projectResponse?.projectColor || theme.palette.primary.main,
    )
    const [showColorPicker, setShowColorPicker] = useState(false)
    const colorPickerRef = React.useRef<HTMLDivElement>(null)
    const [projectStartDate, setProjectStartDate] = useState<Date | null>(
        projectResponse?.projectStartDate
            ? new Date(projectResponse.projectStartDate)
            : new Date(),
    )
    const [projectEndDate, setProjectEndDate] = useState<Date | null>(
        projectResponse?.projectEndDate
            ? new Date(projectResponse.projectEndDate)
            : new Date(new Date().setMonth(new Date().getMonth() + 1)),
    )

    const [applyMoveOutRange, setApplyMoveOutRange] = useState(
        projectResponse?.apply_move_out_range ?? false,
    )
    const [moveOutOffset, setMoveOutOffset] = useState(
        projectResponse?.move_out_offset ?? 30,
    )
    const [autoSyncBedStatus, setAutoSyncBedStatus] = useState(true)
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)

    const [
        selectedInspection,
        setSelectedInspection,
    ] = useState<Inspection | null>(null)

    const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null)

    // current set of apartments project ids. These ids need to be unique.
    // Therefore this list will be used to prompt the user to select a unique id
    // if the try to enter a projectId that is already in the list.
    const projectIds = projectResponse?.project_ids || []

    const [projectIdError, setProjectIdError] = useState('')

    const validateProjectId = (id: string) => {
        if (projectIds.includes(id)) {
            setProjectIdError(
                'This project ID already exists. Please choose a unique ID.',
            )
            return false
        }
        if (id.length >= 9) {
            setProjectIdError('Project ID must be less than 9 characters.')
            return false
        }
        setProjectIdError('')
        return true
    }

    const handleProjectIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newId = e.target.value
        setProjectId(newId)
        validateProjectId(newId)
    }

    const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Only allow numbers, commas, and decimal points
        const value = e.target.value.replace(/[^0-9.,]/g, '')

        // Remove existing formatting (commas)
        const cleanValue = value.replace(/,/g, '')

        // Format with thousands separators
        if (cleanValue) {
            // Handle decimal part separately
            const parts = cleanValue.split('.')
            const integerPart = parts[0]
            const decimalPart = parts.length > 1 ? '.' + parts[1] : ''

            // Add thousands separators to integer part
            const formattedInteger = integerPart.replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ',',
            )

            // Combine parts
            setProjectBudget(formattedInteger + decimalPart)
        } else {
            setProjectBudget('')
        }
    }

    const formatBudget = (budget: string) => {
        if (!budget) return '$0.00'
        const cleanBudget = budget.replace(/[^\d.-]/g, '')
        const numberValue = Number(cleanBudget)
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(numberValue)
    }

    const budgetNumber = Number(
        projectBudget.replace(/,/g, '').replace('$', ''),
    )

    const handleSubmit = () => {
        // Validate form
        if (!projectName.trim()) {
            alert('Please enter a project name')
            return
        }

        if (!projectId.trim()) {
            alert('Please enter a project ID')
            return
        }

        if (projectIds.includes(projectId.trim())) {
            alert('This project ID already exists. Please choose a unique ID.')
            return
        }

        if (projectId.trim().length > 9) {
            alert('Project ID must be less than 9 characters.')
            return
        }

        if (!projectStartDate || !projectEndDate) {
            alert('Please select both start and end dates')
            return
        }

        if (projectEndDate < projectStartDate) {
            alert('End date cannot be before start date')
            return
        }
        createSchedule({
            body: {
                name: projectName,
                schedule_id: projectId,
                baseline_budget: budgetNumber,
                color: projectColor,
                // Date-only: backend applies property-local day bounds.
                start_date: toYYYYMMDD(projectStartDate),
                end_date: toYYYYMMDD(projectEndDate),
                inspection: selectedInspection?.id,
                last_sync_date: lastSyncDate?.toISOString() ?? undefined,
                apply_move_out_range: applyMoveOutRange,
                move_out_offset: moveOutOffset,
            },
        }).then((response) => {
            const schedule: Schedule = response.data
            const isAppFolioProperty =
                workspaceUser?.active_workspace.is_appfolio_property
            const isEntrataIntegrated =
                workspaceUser?.active_workspace.entrata_integrated

            if (autoSyncBedStatus && isAppFolioProperty) {
                Promise.all([
                    getUnitReport(schedule),
                    syncBedStatusesFromLeases(schedule),
                ]).then(() => {
                    setShouldRefresh(true)
                })
            } else if (autoSyncBedStatus && isEntrataIntegrated) {
                Promise.all([
                    getUnitReport(schedule),
                    syncBedStatuses(schedule),
                ]).then(() => {
                    setShouldRefresh(true)
                })
            } else {
                setShouldRefresh(true)
            }
        })

        // Create a message with all the project information
        const projectInfo = `A project with the following properties was created:
            - Name: ${projectName}
            - Project ID: ${projectId}
            - Budget: ${formatBudget(projectBudget)}
            - Color: ${projectColor}
            - Start Date: ${projectStartDate?.toLocaleDateString()}
            - End Date: ${projectEndDate?.toLocaleDateString()}

        Please explain to the user that you can help with scheduling their newly
        created project. Or that you are able to create a plan for their project
        which is a detailed checklist of tasks that need to be completed prior to
        the start of the project. These plans help the user ensure their project
        runs smoothly and on time.

        `

        // Send the message to Lori
        handleSendMessage(projectInfo, 'system')
        setThinking(true)
    }

    // Add these handler functions in the component body
    const toggleColorPicker = (e: React.MouseEvent) => {
        // Stop event propagation to prevent conflicts with the click-outside handler
        e.stopPropagation()
        setShowColorPicker(!showColorPicker)
    }

    // Modify the click-outside handler
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // Make sure we're not handling clicks on the button itself
            const buttonElement = document.getElementById(
                'color-picker-toggle-btn',
            )
            if (
                colorPickerRef.current &&
                !colorPickerRef.current.contains(event.target as Node) &&
                buttonElement !== event.target &&
                !buttonElement?.contains(event.target as Node)
            ) {
                setShowColorPicker(false)
            }
        }

        if (showColorPicker) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showColorPicker])

    const validInspections = useMemo(
        () =>
            inspectionList?.filter(
                (inspection) => inspection.active && !inspection.schedule,
            ),
        [],
    )

    return (
        <Card className={classes.root} style={{ borderColor: projectColor }}>
            <CardContent className={classes.content}>
                <div className={classes.header}>
                    <Typography className={classes.projectName}>
                        Create New Project
                    </Typography>
                </div>

                <TextField
                    className={classes.formField}
                    label="Project Name"
                    variant="outlined"
                    fullWidth
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                />

                <TextField
                    className={classes.formField}
                    label="Project ID"
                    variant="outlined"
                    fullWidth
                    value={projectId}
                    onChange={handleProjectIdChange}
                    error={!!projectIdError}
                    helperText={projectIdError}
                />

                {validInspections && (
                    <Selector
                        data={validInspections}
                        currentValue={selectedInspection?.id ?? ''}
                        onChange={(e) => {
                            const selectedId = e.target.value as number
                            const selected =
                                validInspections.find(
                                    (inspection) =>
                                        inspection.id === selectedId,
                                ) ?? null
                            setSelectedInspection(selected)
                        }}
                        getDisplayString={(inspection) => inspection.name}
                        label="Inspection"
                        customStyle={{
                            formControl: { marginBottom: theme.spacing(2) },
                        }}
                    />
                )}

                <TextField
                    className={classes.formField}
                    label="Project Budget"
                    variant="outlined"
                    fullWidth
                    value={projectBudget}
                    onChange={handleBudgetChange}
                    placeholder="0.00"
                    InputProps={{
                        startAdornment: <span>$</span>,
                    }}
                />

                <FormControl className={classes.colorPickerContainer}>
                    <Typography variant="body2">Project Color</Typography>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                            marginTop: theme.spacing(1),
                            height: '20px',
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        <div
                            className={classes.colorPreview}
                            style={{
                                backgroundColor: projectColor,
                                width: 'calc(50% - 4px)',
                                flex: 'none',
                                height: '100%',
                                marginTop: 0,
                            }}
                        />
                        <Button
                            id="color-picker-toggle-btn"
                            variant="outlined"
                            size="small"
                            onClick={toggleColorPicker}
                            style={{
                                textTransform: 'none',
                                width: 'calc(50% - 4px)',
                                flex: 'none',
                                height: '100%',
                                minHeight: 'unset',
                                padding: '0 8px',
                                lineHeight: 1,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                            }}
                        >
                            {showColorPicker
                                ? 'Set Color...'
                                : 'Select Color...'}
                        </Button>
                    </div>
                    {showColorPicker && (
                        <div style={{ marginTop: 8 }} ref={colorPickerRef}>
                            <ChromePicker
                                color={projectColor}
                                onChange={(color) => setProjectColor(color.hex)}
                                disableAlpha
                            />
                        </div>
                    )}
                </FormControl>

                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <div className={classes.datePickers}>
                        <DatePicker
                            className={classes.datePicker}
                            label="Start Date"
                            value={projectStartDate}
                            onChange={(date) => {
                                setProjectStartDate(date ?? new Date())
                                if (
                                    workspaceUser?.active_workspace
                                        .entrata_integrated &&
                                    !lastSyncDate
                                ) {
                                    setLastSyncDate(date ?? new Date())
                                }
                            }}
                            format="MM/dd/yyyy"
                            fullWidth
                            inputVariant="outlined"
                        />
                        <DatePicker
                            className={classes.datePicker}
                            label="End Date"
                            value={projectEndDate}
                            onChange={setProjectEndDate}
                            format="MM/dd/yyyy"
                            fullWidth
                            inputVariant="outlined"
                            minDate={projectStartDate || undefined}
                        />
                    </div>
                </MuiPickersUtilsProvider>

                {workspaceUser?.active_workspace.entrata_integrated && (
                    <KeyboardDatePicker
                        value={lastSyncDate}
                        onChange={setLastSyncDate}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="Last Bed Status Sync Date"
                        style={{ marginTop: theme.spacing(2) }}
                    />
                )}

                {/* Advanced Settings Dropdown */}
                {workspaceUser?.active_workspace.entrata_integrated && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <Container
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: theme.spacing(1, 0),
                            }}
                            onClick={() =>
                                setAdvancedSettingsOpen(!advancedSettingsOpen)
                            }
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    color: theme.palette.grey[700],
                                    flex: 1,
                                }}
                            >
                                Advanced Settings
                            </span>
                            {advancedSettingsOpen ? (
                                <ExpandLessIcon
                                    style={{ color: theme.palette.grey[600] }}
                                />
                            ) : (
                                <ExpandMoreIcon
                                    style={{ color: theme.palette.grey[600] }}
                                />
                            )}
                        </Container>

                        <Collapse in={advancedSettingsOpen}>
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    paddingLeft: theme.spacing(2),
                                }}
                            >
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    <Container
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.body2,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightLight,
                                                color: theme.palette.grey[600],
                                            }}
                                        >
                                            Auto-Detect Move Outs
                                        </span>
                                        <Tooltip
                                            title={
                                                <div
                                                    style={{
                                                        fontSize: '14px',
                                                        lineHeight: '1.4',
                                                    }}
                                                >
                                                    <strong>
                                                        Move Out Offset:
                                                    </strong>{' '}
                                                    Number of days before the
                                                    schedule start date to check
                                                    for ending leases.
                                                    <br />
                                                    <br />
                                                    When syncing bed statuses,
                                                    leases ending within this
                                                    timeframe will be
                                                    automatically marked as
                                                    vacant. All other leases
                                                    that are not renewals or
                                                    transfers will be marked as
                                                    DNT.
                                                </div>
                                            }
                                        >
                                            <InfoIcon
                                                style={{
                                                    fontSize: 20,
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    cursor: 'help',
                                                }}
                                            />
                                        </Tooltip>
                                    </Container>
                                    <Container
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginBottom: theme.spacing(2),
                                        }}
                                    >
                                        <Switch
                                            checked={applyMoveOutRange}
                                            color="primary"
                                            onClick={() => {
                                                setApplyMoveOutRange(
                                                    !applyMoveOutRange,
                                                )
                                            }}
                                        />
                                        <TextField
                                            value={moveOutOffset}
                                            onChange={(e) =>
                                                setMoveOutOffset(
                                                    Number(e.target.value),
                                                )
                                            }
                                            style={{ width: '100%' }}
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            label="Check Last X Days"
                                            disabled={!applyMoveOutRange}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Container>
                                </Container>

                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    <Container
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.body2,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightLight,
                                                color: theme.palette.grey[600],
                                            }}
                                        >
                                            Run Initial Bed Status Sync
                                        </span>
                                        <Tooltip
                                            title={
                                                'Are you sure you want to sync your bed statuses?\n\n' +
                                                'This action can be destructive. If you have moved your leases out in Entrata ' +
                                                'the bed statuses will be marked as vacant when renewal is what you expect.\n\n' +
                                                'This action will download a unit report.'
                                            }
                                        >
                                            <InfoIcon
                                                style={{
                                                    fontSize: 20,
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    cursor: 'help',
                                                }}
                                            />
                                        </Tooltip>
                                    </Container>
                                    <Switch
                                        checked={autoSyncBedStatus}
                                        color="primary"
                                        onClick={() => {
                                            setAutoSyncBedStatus(
                                                !autoSyncBedStatus,
                                            )
                                        }}
                                    />
                                </Container>
                            </Container>
                        </Collapse>
                    </Container>
                )}

                <div className={classes.buttonContainer}>
                    <Button
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                        disabled={
                            !projectName ||
                            !projectId ||
                            !projectStartDate ||
                            !projectEndDate
                        }
                    >
                        Create Project
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
